import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import { BalloonIcon, EditFormIcon, MessageIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import WeeklyPlanner from '@repo/ui/weekly-planner'
import { useMutation, useQuery } from '@tanstack/react-query'
import { forwardRef, useImperativeHandle } from 'react'
import { toast } from 'sonner'

import { createCenterActivity } from '../../../../../api/centers/center-activities/create-center-activity'
import { deleteCenterActivity } from '../../../../../api/centers/center-activities/delete-center-activity'
import { getCenterActivities } from '../../../../../api/centers/center-activities/get-center-activities'
import { updateCenterActivity } from '../../../../../api/centers/center-activities/update-center-activity'
import { createCenterCourse } from '../../../../../api/centers/center-courses/create-center-course'
import { getCenterCourses } from '../../../../../api/centers/center-courses/get-center-courses'
import { getCenterActions } from '../../../../../api/centers/get-center-actions'
import { useObserver } from '../../../../../lib/contexts/observer'
import { useModalManager } from '../../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../../lib/hooks/use-translate'
import { CenterStatus } from '../../../../../lib/types/enums/center/center-status'
import { CurrencyEnum } from '../../../../../lib/types/enums/common/currency.enum'
import { CenterActivity } from '../../../../../lib/types/models/center-activity/center-activity'
import { CenterCourse } from '../../../../../lib/types/models/center-course/center-course'
import { modalButtonConfig } from '../../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../../lib/utils/functions/misc.functions'
import { queryClient } from '../../../../../main'
import { isHostFamilyDayActivity } from '../../../../../utils/constants/activity.constants'
import { ItemNotFound } from '../../../../elements/item-not-found'
import { CenterCosts } from '../../../centers/center-costs'
import CenterActivityForm from '../../../ol-personal-space/ol-personal-space-center-program/ol-personal-space-activity-form'
import CenterActivityView from '../../../ols/centers/center-tab-views/center-program/center-activity-view'
import OlCenterProgramDetails from '../../../ols/centers/center-tab-views/center-program/ol-center-program-details'
import { mapCenterActivityToInput } from '../../utils'
import CenterCourseForm from '../center-course-form'
import CenterCourseEditFormModal from '../center-course-form-modal'

export type PlannerHandle = {
  openCreateCourseModal: () => void
  openCreateActivityModal: () => void
}

export type OlCenterWeeklyPlannerProps = {
  centerId: number
  olId?: number
  startDate: Date
  endDate: Date
  currency?: CurrencyEnum
  centerActivityStatus: CenterStatus
  refetch?: () => Promise<unknown>
  allowCourseDeletion?: boolean
  mode: 'open' | 'validate' | 'view' | 'staff-view'
}

const OlCenterWeeklyPlanner = forwardRef<PlannerHandle, OlCenterWeeklyPlannerProps>(
  (
    {
      centerId: id,
      olId,
      startDate,
      endDate,
      currency,
      centerActivityStatus,
      mode,
      refetch,
      allowCourseDeletion = true
    },
    ref
  ) => {
    const olCenterActivityCrud = useObserver({ key: 'ol-center-activity-crud' })
    const { t } = useTranslate()

    const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
      'create' | 'edit',
      CenterCourse
    >(['create', 'edit'])

    const {
      selectedData: selectedActivity,
      modalState: activityModalState,
      toggleModal: activityToggleModal,
      closeModel: activityCloseModal
    } = useModalManager<'create' | 'edit' | 'view', CenterActivity>(['view', 'create', 'edit'])

    useImperativeHandle(ref, () => ({
      openCreateCourseModal: () => toggleModal('create'),
      openCreateActivityModal: () => activityToggleModal('create')
    }))

    const { data: centerActions } = useQuery({
      queryKey: ['center-actions', id],
      queryFn: () => getCenterActions(id),
      enabled: !!id
    })

    const canViewActivitiesAndBudget =
      centerActions && centerActions.canViewCenterActivitiesAndBudget

    const { data: courses } = useQuery({
      queryKey: ['center-courses', id],
      queryFn: () => getCenterCourses(id),
      enabled: !!id && canViewActivitiesAndBudget
    })

    const { data: activities } = useQuery({
      queryKey: ['center-activities', id],
      queryFn: () => getCenterActivities(id),
      enabled: !!id && canViewActivitiesAndBudget
    })

    const createActivity = useMutation({
      mutationKey: ['create-center-activity', id],
      mutationFn: createCenterActivity,
      onSuccess: () => {
        toast.success(t('ol.center.program.activities.planner.activity.create.success.message'))
        queryClient.invalidateQueries({ queryKey: ['center-activities', id] })
        olCenterActivityCrud.notify(['ol-center-activity-table'])
        activityCloseModal('create')
      },
      onError: showAxiosError
    })

    const updateActivity = useMutation({
      mutationKey: ['update-center-activity', selectedActivity?.id],
      mutationFn: updateCenterActivity,
      onSuccess: () => {
        toast.success(t('ol.center.program.activities.planner.activity.edit.success.message'))
        queryClient.invalidateQueries({ queryKey: ['center-activities', id] })
        olCenterActivityCrud.notify(['ol-center-activity-table'])
        activityCloseModal('edit')
      },
      onError: showAxiosError
    })

    const deleteActivity = useMutation({
      mutationKey: ['delete-center-activity', selectedActivity?.id],
      mutationFn: deleteCenterActivity,
      onSuccess: () => {
        toast.success(t('ol.center.program.activities.planner.activity.delete.success.message'))
        queryClient.invalidateQueries({ queryKey: ['center-activities', id] })
        olCenterActivityCrud.notify(['ol-center-activity-table'])
        //delete is inside the edit modal, so we close it after deletion
        activityCloseModal('edit')
      },
      onError: showAxiosError
    })

    const createCourse = useMutation({
      mutationKey: ['create-center-course', id],
      mutationFn: createCenterCourse,
      onSuccess: () => {
        toast.success(t('ol.center.program.activities.planner.course.create.success.message'))
        queryClient.invalidateQueries({ queryKey: ['center-courses', id] })
        closeModel('create')
      },
      onError: showAxiosError
    })

    const isPlannerDisabled =
      (mode === 'open' && centerActivityStatus !== CenterStatus.OPEN) ||
      (mode === 'validate' && centerActivityStatus === CenterStatus.VALIDATED) ||
      mode === 'view' ||
      mode === 'staff-view'

    if (!canViewActivitiesAndBudget)
      return (
        <ItemNotFound
          title={t('staff.personal.space.my.journeys.details.cannot.view.activities.and.budget')}
        />
      )

    return (
      <div className="flex flex-col gap-3">
        <WeeklyPlanner
          startDate={new Date(startDate)}
          endDate={new Date(endDate)}
          disabled={isPlannerDisabled}
          items={[
            ...(courses?.data ?? []).map((course) => ({
              id: course.id,
              type: 'course',
              content: (
                <Typography
                  variant="small-medium"
                  color={tailwindColors.vividBlue.light.active}
                  className="mb-4"
                >
                  {t('ol.center.program.activities.planner.break.included')}
                </Typography>
              ),
              disabled: isPlannerDisabled,
              startingTime: course.startingTime,
              endingTime: course.endingTime,
              title: t('ol.center.program.activities.planner.course'),
              color: tailwindColors.vividBlue.DEFAULT,
              showHours: true,
              backgroundColor: tailwindColors.vividBlue.light.DEFAULT,
              borderColor: tailwindColors.vividBlue.light.active,
              onClick: () => toggleModal('edit', course)
            })),
            ...(activities?.data ?? []).map((activity) => ({
              id: activity.id,
              type: 'activity',
              disabled: isPlannerDisabled && mode === 'open',
              startingTime: activity.startingTime,
              endingTime: activity.endingTime,
              title: activity.activity.name,
              showHours: false,
              showTime: !isHostFamilyDayActivity(activity.activity),
              color: tailwindColors.fushia.DEFAULT,
              backgroundColor: tailwindColors.fushia.light.DEFAULT,
              borderColor: tailwindColors.fushia.light.active,
              icon: activity.comment ? MessageIcon : undefined,
              onClick: () => {
                if (mode === 'open') activityToggleModal('edit', activity)
                else activityToggleModal('view', activity)
              }
            }))
          ]}
          labels={[
            {
              date: new Date(startDate),
              element: (
                <Badge variant="primary" text={t('ol.center.program.activities.planner.arival')} />
              )
            },
            {
              date: new Date(endDate),
              element: (
                <Badge
                  variant="primary"
                  text={t('ol.center.program.activities.planner.departure')}
                />
              )
            }
          ]}
        />
        {mode !== 'view' && (
          <>
            <CenterCosts.Status
              id={id}
              costType="activities-cost"
              refetch={async () => {
                await refetch?.()
              }}
            />
            <OlCenterProgramDetails centerId={id} mode={mode} currency={currency} />
          </>
        )}

        {olId && (
          <Modal
            isOpen={activityModalState.create}
            title={t('ol.center.program.activities.planner.activity.form.create.title')}
            icon={BalloonIcon}
            hasBorderBottom
            hideActions
            size="medium"
            draggable
            onCancel={() => activityCloseModal('create')}
          >
            <CenterActivityForm
              olId={olId}
              centerId={id}
              className={MODAL_CONTENT_CLASSNAME}
              buttonsConfig={modalButtonConfig({
                confirmText: t('button.confirm'),
                cancelText: t('button.close'),
                isLoading: createActivity.isPending,
                onCancel: () => activityCloseModal('create'),
                onConfirm: async (values) => {
                  await createActivity.mutateAsync({ centerId: id, ...values })
                  activityCloseModal('create')
                }
              })}
            />
          </Modal>
        )}

        {selectedData && modalState.edit && (
          <CenterCourseEditFormModal
            centerId={id}
            startDate={startDate ? new Date(startDate) : undefined}
            endDate={endDate ? new Date(endDate) : undefined}
            data={selectedData}
            closeModel={() => closeModel('edit')}
            isOpen={modalState.edit}
            allowDelete={allowCourseDeletion}
          />
        )}

        {selectedActivity && olId && (
          <Modal
            isOpen={activityModalState.edit}
            title={t('ol.center.program.activities.planner.activity.form.edit.title')}
            icon={BalloonIcon}
            hasBorderBottom
            hideActions
            size="medium"
            draggable
            onCancel={() => activityCloseModal('edit')}
          >
            <CenterActivityForm
              olId={olId}
              centerId={id}
              className={MODAL_CONTENT_CLASSNAME}
              defaultValues={mapCenterActivityToInput(selectedActivity)}
              buttonsConfig={modalButtonConfig({
                confirmText: t('button.confirm'),
                cancelText: t('button.close'),
                deleteText: t('ol.center.program.activities.planner.activity.form.delete.title'),
                onCancel: () => activityCloseModal('edit'),
                isLoading: updateActivity.isPending,
                isDeleteLoading: deleteActivity.isPending,
                onConfirm: async (values) => {
                  await updateActivity.mutateAsync({
                    id: selectedActivity.id,
                    centerId: id,
                    ...values
                  })
                  activityCloseModal('edit')
                },
                onDelete: async () => {
                  await deleteActivity.mutateAsync({ centerId: id, id: selectedActivity.id })
                }
              })}
            />
          </Modal>
        )}

        <Modal
          isOpen={modalState.create}
          title={t('ol.center.program.activities.planner.course.form.create.title')}
          icon={EditFormIcon}
          hasBorderBottom
          hideActions
          size="medium"
          onCancel={() => closeModel('create')}
        >
          <CenterCourseForm
            className={MODAL_CONTENT_CLASSNAME}
            startDate={startDate ? new Date(startDate) : undefined}
            endDate={endDate ? new Date(endDate) : undefined}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('create'),
              isLoading: createCourse.isPending,
              onConfirm: async (values) => {
                await createCourse.mutateAsync({ centerId: id, ...values })
              }
            })}
          />
        </Modal>

        {selectedData && modalState.edit && (
          <CenterCourseEditFormModal
            data={selectedData}
            startDate={startDate ? new Date(startDate) : undefined}
            endDate={endDate ? new Date(endDate) : undefined}
            centerId={id}
            closeModel={() => closeModel('edit')}
            allowDelete={allowCourseDeletion}
            isOpen={modalState.edit}
          />
        )}

        {selectedActivity && (
          <Modal
            isOpen={activityModalState.view}
            title={selectedActivity.activity.name}
            icon={BalloonIcon}
            hasBorderBottom
            hideActions
            size="medium"
            onCancel={() => activityCloseModal('view')}
          >
            <CenterActivityView
              className={MODAL_CONTENT_CLASSNAME}
              centerActivity={selectedActivity}
            />
          </Modal>
        )}
      </div>
    )
  }
)

export default OlCenterWeeklyPlanner
